import React from "react";
import {
  FiAtSign,
  FiBriefcase,
  FiCalendar,
  FiCreditCard,
  FiFileText,
  FiHash,
  FiMail,
  FiMapPin,
  FiPhone,
  FiSearch,
  FiUpload,
  FiUser,
} from "react-icons/fi";

import styles from "./new.module.css";
import { getRegExp, getSentenceFromCamelCase } from "./Helpers";
import { InputProps } from "../../types/form";

type ModernInputProps = InputProps & {
  leftIcon?: React.ReactNode;
};

const getInputIcon = (name = "", type = "") => {
  const key = `${name} ${type}`.toLowerCase();

  if (type === "file") return <FiUpload />;
  if (type === "search") return <FiSearch />;
  if (type === "date") return <FiCalendar />;
  if (type === "email" || key.includes("email")) return <FiMail />;
  if (type === "tel" || key.includes("phone") || key.includes("mobile")) {
    return <FiPhone />;
  }
  if (key.includes("name") || key.includes("user")) return <FiUser />;
  if (key.includes("address") || key.includes("location")) return <FiMapPin />;
  if (key.includes("account") || key.includes("bvn")) return <FiCreditCard />;
  if (key.includes("amount") || key.includes("loan")) return <FiBriefcase />;
  if (key.includes("number") || key.includes("code")) return <FiHash />;
  if (key.includes("description") || key.includes("comment")) {
    return <FiFileText />;
  }

  return <FiAtSign />;
};

const Input = ({
  checked,
  className,
  disabled,
  error,
  fixLength,
  required,
  label,
  minLength,
  maxLength,
  name,
  onChange,
  placeholder,
  reqType,
  style,
  type = "text",
  validationHandler,
  value,
  size = "medium",
  labelClassName,
  leftIcon,
}: ModernInputProps) => {
  const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked, files } = event.target;

    let inputValue: string | boolean | File | undefined;

    if (type === "file") {
      inputValue = files?.[0];
    } else if (type === "checkbox") {
      inputValue = checked;
    } else {
      inputValue = value;
    }

    onChange && onChange(name, inputValue);
  };

  const onValidationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!validationHandler) return;

    const { value } = event.target;
    let errorMessage = "";

    if (!value && required) {
      errorMessage = `Please enter ${getSentenceFromCamelCase(name)}.`;
    } else if (minLength && value.length < minLength) {
      errorMessage = `${
        name.charAt(0).toUpperCase() + getSentenceFromCamelCase(name).slice(1)
      } must be at least ${minLength} characters long.`;
    } else if (maxLength && value.length > maxLength) {
      errorMessage = `${
        name.charAt(0).toUpperCase() + getSentenceFromCamelCase(name).slice(1)
      } must be ${maxLength} characters long.`;
    } else if (fixLength && value.length !== fixLength) {
      errorMessage = `${
        name.charAt(0).toUpperCase() + getSentenceFromCamelCase(name).slice(1)
      } must be ${fixLength} characters.`;
    } else if (value && reqType) {
      const regExp = getRegExp(reqType) as RegExp;

      if (!regExp.test(value)) {
        errorMessage = `Please enter valid ${getSentenceFromCamelCase(name)}.`;
      }
    }

    validationHandler(name, errorMessage);
  };

  return (
    <div
      className={`${styles.InputContainer} ${size ? styles[size] : ""} ${
        error ? styles.hasError : ""
      }`}
    >
      <label htmlFor={name} className={labelClassName || styles.inputLabel}>
        {label} {required && <span className={styles.required}>&nbsp;*</span>}
      </label>

      <div className={styles.fieldShell}>
        <span className={styles.fieldIcon} aria-hidden="true">
          {leftIcon || getInputIcon(name, type)}
        </span>

        <input
          type={type}
          name={name}
          id={name}
          value={type === "file" ? undefined : (value as string)}
          checked={checked}
          placeholder={placeholder}
          className={`${styles.blackBorder} ${styles.modernInput} ${
            className || ""
          } ${error ? styles.borderRed : ""}`}
          required={required}
          style={style}
          disabled={disabled}
          onChange={onChangeHandler}
          onBlur={onValidationChange}
        />
      </div>

      {error ? (
        <span className={`${styles.required} ${styles.textSmall}`}>
          {error}
        </span>
      ) : null}
    </div>
  );
};

export default Input;
